import { get, set } from 'lodash';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { ObjectKeys } from '@/lib/utils';
import type {
  Filter,
  FilterEnumChoice,
  FilterOperator,
  FilterValue,
} from './constants';
import { DateTime } from 'luxon';

/**
 * Handles removing a filter choice from the filter copy
 */
export const handleRemoveFilter = ({
  choice,
  filterCopy,
  filterVariant,
}: {
  choice: FilterEnumChoice;
  filterCopy: JsonFilter;
  filterVariant: Filter;
}): void => {
  choice.filters.forEach((f) => {
    const externalFieldName =
      f.fieldName ??
      choice.overrideExternalFieldName ??
      filterVariant.externalFieldName;

    const prevValue = get(filterCopy, `${externalFieldName}.${f.operator}`);

    if (Array.isArray(prevValue)) {
      const filtered = (prevValue ?? []).filter((p) => p != f.value);

      if (filtered.length) {
        set(filterCopy, `${externalFieldName}.${f.operator}`, filtered);
      } else {
        if (filterCopy[externalFieldName]?.[f.operator])
          delete filterCopy[externalFieldName][f.operator];

        // clean filter if no more operators for external field name
        if (ObjectKeys(filterCopy[externalFieldName] ?? {}).length === 0) {
          delete filterCopy[externalFieldName];
        }
      }
    }
  });
};

/**
 * Handles adding a filter choice to the filter copy
 */
export const handleAddFilter = ({
  choice,
  filterCopy,
  filterVariant,
}: {
  choice: FilterEnumChoice;
  filterCopy: JsonFilter;
  filterVariant: Filter;
}): void => {
  choice.filters.forEach((f) => {
    const externalFieldName =
      f.fieldName ??
      choice.overrideExternalFieldName ??
      filterVariant.externalFieldName;

    const prevValue = get(filterCopy, `${externalFieldName}.${f.operator}`);
    set(filterCopy, `${externalFieldName}.${f.operator}`, [
      ...(prevValue && Array.isArray(prevValue) ? prevValue : []),
      f.value,
    ]);
  });
};

/**
 * Checks if a filter choice is currently active
 */
export const getFilterIsActive = (
  filterVariant: Filter,
  choice: FilterEnumChoice,
  filter: JsonFilter | null
): boolean => {
  const choiceIsSelected = choice.filters.every((cur) => {
    const externalFieldName =
      cur.fieldName ??
      choice.overrideExternalFieldName ??
      filterVariant.externalFieldName;

    const current = externalFieldName ? filter?.[externalFieldName] : undefined;
    if (!current) return false;
    const currentVal = get(current, cur.operator);
    return Array.isArray(currentVal) && currentVal.includes(cur.value);
  });

  return choiceIsSelected;
};

export function convertDateFilterValue(
  oldOperator: FilterOperator,
  newOperator: FilterOperator,
  value: FilterValue
): FilterValue {
  const isRelative = (op: FilterOperator) =>
    op === '__date_time_past' ||
    op === '__date_time_future' ||
    op === '__date_past' ||
    op === '__date_future';

  const todayIso = DateTime.now().toISODate();
  const today = DateTime.fromISO(todayIso ?? '', { zone: 'utc' }).startOf(
    'day'
  );

  const toNumber = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(1, Math.round(n)) : null;
  };

  const parseDate = (v: unknown): DateTime | null => {
    if (typeof v !== 'string') return null;
    const d = DateTime.fromISO(v, { zone: 'utc' });
    return d.isValid ? d.startOf('day') : null;
  };

  if (isRelative(oldOperator) && !isRelative(newOperator)) {
    const n = toNumber(value);
    if (n === null) return '';

    if (oldOperator === '__date_time_past' || oldOperator === '__date_past') {
      return today.minus({ days: n - 1 }).toISODate();
    }

    if (
      oldOperator === '__date_time_future' ||
      oldOperator === '__date_future'
    ) {
      return today.plus({ days: n }).toISODate();
    }
  }

  if (!isRelative(oldOperator) && isRelative(newOperator)) {
    const dateValue = parseDate(value);
    if (!dateValue) return '';

    if (newOperator === '__date_time_past' || newOperator === '__date_past') {
      if (dateValue > today) return '';
      return Math.floor(today.diff(dateValue, 'days').days) + 1;
    }

    if (
      newOperator === '__date_time_future' ||
      newOperator === '__date_future'
    ) {
      if (dateValue <= today) return '';
      return Math.floor(dateValue.diff(today, 'days').days);
    }
  }

  return value ?? '';
}
